import { ACCESS_REVIEWER_ROLE, ADMIN_ROLE, AUDITOR_ROLE, VIEWER_ROLE } from '../../../../shared/solution.mjs';
import { GUARDIAN_ROLE, ROLE_DESCRIPTIONS, ROLE_IDS, ROLE_LABELS } from '../../lib/people-rows.js';
import SectionCard from '../../components/SectionCard.jsx';
import StatusWord from '../../components/StatusWord.jsx';

const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : null);

const SOURCE_LABELS = {
  claim: 'recorded at the claim',
  'claim-audit-log': 'recovered from the claim audit log',
  'admin-email-at-backfill': 'recovered from the admin email',
  'id-token': 'from the Microsoft sign-in',
  'entra-audit-log': "recovered from the customer's Entra audit log",
  'tenant-proof': 'object ID only, from the tenant check',
};

function Fact({ label, children }) {
  return (
    <>
      <dt className="text-ipc-muted">{label}</dt>
      <dd className="break-all">{children}</dd>
    </>
  );
}

function OnboardingCard({ onboarding }) {
  const { claim, consentAdmin, consentVerifiedAt, tenantApproval } = onboarding;
  return (
    <SectionCard title="Onboarding" headingLevel={3} as="div" testId="onboarding-card">
      <dl className="grid grid-cols-[max-content_1fr] gap-x-8 gap-y-2 text-sm">
        <Fact label="Claimed by">
          {claim.email
            ? <>{claim.email}{claim.at && <span className="text-ipc-muted"> on {fmt(claim.at)}</span>}</>
            : <span className="text-ipc-muted">Not recorded{claim.at ? ` (link claimed ${fmt(claim.at)})` : ' (created by an operator)'}</span>}
          {claim.source && claim.source !== 'claim' && <span className="block text-xs text-ipc-muted">{SOURCE_LABELS[claim.source] ?? claim.source}</span>}
        </Fact>
        <Fact label="Consent granted by">
          {consentAdmin
            ? (
              <>
                {consentAdmin.upn ?? <span className="font-mono text-xs">{consentAdmin.oid}</span>}
                {consentAdmin.at && <span className="text-ipc-muted"> on {fmt(consentAdmin.at)}</span>}
                {consentAdmin.upn && consentAdmin.oid && <span className="block font-mono text-xs text-ipc-muted">{consentAdmin.oid}</span>}
                {consentAdmin.source && <span className="block text-xs text-ipc-muted">{SOURCE_LABELS[consentAdmin.source] ?? consentAdmin.source}</span>}
                {consentAdmin.currentTenant === false && <span className="block text-xs"><StatusWord tone="warn" strong={false}>For a previous Entra tenant of this customer.</StatusWord></span>}
              </>
            )
            : <span className="text-ipc-muted">Not recorded</span>}
        </Fact>
        {consentVerifiedAt && <Fact label="Consent verified">{fmt(consentVerifiedAt)}</Fact>}
        {tenantApproval && <Fact label="Tenant approved by">{tenantApproval.by}{tenantApproval.at && <span className="text-ipc-muted"> on {fmt(tenantApproval.at)}</span>}</Fact>}
      </dl>
    </SectionCard>
  );
}

export function accountLabel(account) {
  if (!account) return 'No account found';
  if (account.enabled === false) return 'Disabled';
  switch (account.status) {
    case 'FORCE_CHANGE_PASSWORD': return 'Invited, not signed in yet';
    case 'CONFIRMED': return 'Has signed in';
    case 'EXTERNAL_PROVIDER': return 'Signs in with SSO';
    case 'RESET_REQUIRED': return 'Password reset required';
    default: return account.status ?? 'Unknown';
  }
}

/**
 * True only when the server says the central IdP has registered the Auditor role AND the caller may give it (an
 * operator who is not the customer's admin may not). A missing capability fails closed: not selectable.
 */
export const auditorSelectableIn = (data) => data?.capabilities?.auditorRoleRegistered === true && data?.capabilities?.canGrantAuditor !== false;

/** True only when the server says the central IdP has registered the Viewer role. A missing capability fails closed. */
export const viewerSelectableIn = (data) => data?.capabilities?.viewerRoleRegistered === true;

/**
 * The one list of everything that can be assigned to a person, in display order: the portal roles (stored on the
 * central IdP) and Guardian (stored in the Watch settings). The Invite and the Manage roles dialogs both render
 * from this, so a role added to solution.mjs or a Guardian rule appears in both. `held` keeps a role the person
 * already has visible so it can be removed even when it can no longer be given (an unregistered Auditor role).
 */
export function assignableIds({ auditorSelectable = true, viewerSelectable = true, canGuardian = true, held = [] } = {}) {
  return ROLE_IDS.filter((id) => {
    if (id === GUARDIAN_ROLE) return canGuardian;
    if (id === AUDITOR_ROLE) return auditorSelectable || held.includes(id);
    if (id === VIEWER_ROLE) return viewerSelectable || held.includes(id);
    // Given by Access Reviews and kept until removed here; never offered here, a held one stays visible (and kept).
    if (id === ACCESS_REVIEWER_ROLE) return held.includes(id);
    return true;
  });
}

// Shown whenever a Guardian is being added, in both dialogs: Watch checks the address in the directory later.
export const GUARDIAN_DIRECTORY_HINT = 'The collector checks this address in your directory within 15 minutes. Only a valid directory address can receive Watch notices.';

/** One assignable item: checkbox, label and description, the same text wherever it is shown. */
export function RoleOption({ id, idPrefix, checked, disabled = false, note = null, onChange }) {
  return (
    <div className="flex items-start gap-2 text-sm">
      <input id={`${idPrefix}-${id}`} type="checkbox" className="mt-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-ipc-light" checked={checked} disabled={disabled} onChange={(e) => onChange(id, e.target.checked)} />
      <label htmlFor={`${idPrefix}-${id}`}>
        {ROLE_LABELS[id]}
        <span className="block text-xs text-ipc-muted">{ROLE_DESCRIPTIONS[id]}</span>
        {note ? <span className="block text-xs"><StatusWord tone="warn" strong={false}>{note}</StatusWord></span> : null}
      </label>
    </div>
  );
}

/**
 * The role picker of both People dialogs. `selected` is a Set of ids from assignableIds; `onToggle(id, on)`.
 * The Auditor expiry input sits under the Auditor box (`optional` when the role is already held: blank keeps the
 * current end date). `portalNotes` and `watchNotes` are dialog specific hints rendered inside each group.
 */
export function RoleChoices({
  selected, onToggle, idPrefix, ids, portalDisabled = false, guardianDisabled = false, guardianNote = null,
  auditorDays = AUDITOR_DEFAULT_DAYS, onAuditorDays = null, auditorOptional = false, showPortal = true, portalNotes = null, watchNotes = null,
}) {
  const portal = ids.filter((id) => id !== GUARDIAN_ROLE);
  const roles = [...selected];
  return (
    <>
      {showPortal && portal.length > 0 ? (
        <fieldset className="space-y-3">
          <legend className="label">Portal roles</legend>
          {portal.map((id) => (
            <div key={id} className="space-y-1">
              <RoleOption id={id} idPrefix={idPrefix} checked={selected.has(id)} disabled={portalDisabled} onChange={onToggle} />
              {id === AUDITOR_ROLE && selected.has(id) && !portalDisabled && onAuditorDays ? <AuditorExpiry id={`${idPrefix}-auditor-days`} days={auditorDays} onChange={onAuditorDays} optional={auditorOptional} /> : null}
            </div>
          ))}
          <AuditorAdminWarning roles={roles} />
          {portalNotes}
        </fieldset>
      ) : null}
      {ids.includes(GUARDIAN_ROLE) ? (
        <fieldset className="space-y-3">
          <legend className="label">Watch</legend>
          <RoleOption id={GUARDIAN_ROLE} idPrefix={idPrefix} checked={selected.has(GUARDIAN_ROLE)} disabled={guardianDisabled} note={guardianNote} onChange={onToggle} />
          {watchNotes}
        </fieldset>
      ) : null}
    </>
  );
}

// Auditor access always expires (ENTRAID-307): 1 to 180 days, 30 by default. The server holds the same limits.
export const AUDITOR_DEFAULT_DAYS = '30';
const AUDITOR_MAX_DAYS = 180;
const DAY_MS = 24 * 60 * 60 * 1000;
export const validAuditorDays = (days) => /^\d{1,3}$/.test(String(days)) && Number(days) >= 1 && Number(days) <= AUDITOR_MAX_DAYS;
/** ISO expiry `days` from now; the 180 day choice keeps 5 minutes of margin so a fast local clock is not refused. */
export const auditorExpiresAt = (days, now = Date.now()) => new Date(now + Math.min(Number(days) * DAY_MS, AUDITOR_MAX_DAYS * DAY_MS - 5 * 60_000)).toISOString();

/** The expiry control of the Auditor role: days from now, 1 to 180. `optional` (a role already held) leaves it blank to keep the current expiry. */
export function AuditorExpiry({ id, days, onChange, optional = false }) {
  const bad = days !== '' && !validAuditorDays(days);
  return (
    <div className="ml-6 space-y-1 text-sm">
      <label htmlFor={id} className="label">{optional ? 'Change when Auditor access ends (days from now)' : 'Auditor access ends after (days)'}</label>
      <input
        id={id} type="number" inputMode="numeric" min="1" max={AUDITOR_MAX_DAYS} step="1" className="input w-28"
        value={days} onChange={(e) => onChange(e.target.value)} aria-invalid={bad} aria-describedby={`${id}-hint`}
      />
      <p id={`${id}-hint`} className={`text-xs ${bad ? 'text-red-700' : 'text-ipc-muted'}`}>
        {bad ? 'Enter a whole number from 1 to 180.' : `Between 1 and ${AUDITOR_MAX_DAYS} days. Access ends on its own at that time${optional ? '; leave blank to keep the current end date' : ''}.`}
      </p>
    </div>
  );
}

/** Shown when Auditor and Admin are chosen together: the expiry would not limit an admin. */
export function AuditorAdminWarning({ roles }) {
  if (!(roles.includes(AUDITOR_ROLE) && roles.includes(ADMIN_ROLE))) return null;
  return (
    <p role="note" className="ml-6 border-l-4 pl-2 text-xs text-ipc-ink" style={{ borderLeftColor: 'var(--warn)' }}>
      Admin already includes everything an Auditor can read and does not expire. Choose Auditor alone for time-limited, read-only access.
    </p>
  );
}

export const roleLabel = (r) => ROLE_LABELS[r] ?? r;
export { isStaffEmail, manageable, PENDING } from '../../lib/people-rows.js';
export { fmt, SOURCE_LABELS, Fact, OnboardingCard };
